import Head from 'next/head';
import { ReactNode } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

/**
 * Keep reCAPTCHA v3 loaded once for the whole app instead of mounting it per-route.
 * Google recommends avoiding lazy-loading reCAPTCHA right before a protected action,
 * because v3 works better with more page/session context:
 * https://developers.google.com/recaptcha/docs/loading
 * https://developers.google.com/recaptcha/docs/v3
 */
const ReCaptchaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const reCaptchaKey = process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_V3_SITE_KEY || '';

  return (
    <>
      {reCaptchaKey && (
        <Head>
          {/* Google recommends preconnect hints for reCAPTCHA hosts to reduce connection setup cost:
              https://developers.google.com/recaptcha/docs/loading */}
          <link href='https://www.google.com' rel='preconnect' />
          <link crossOrigin='anonymous' href='https://www.gstatic.com' rel='preconnect' />
          <link href='https://www.google.com' rel='dns-prefetch' />
          <link href='https://www.gstatic.com' rel='dns-prefetch' />
        </Head>
      )}
      <GoogleReCaptchaProvider
        reCaptchaKey={reCaptchaKey}
        scriptProps={{
          // Async loading is the recommended default for reCAPTCHA script performance:
          // https://developers.google.com/recaptcha/docs/loading
          async: true,
          defer: true,
          appendTo: 'head',
        }}>
        {children}
      </GoogleReCaptchaProvider>
    </>
  );
};

export default ReCaptchaProvider;
